// views/Settings/GroupOrderSettings.js
// Order together (group baskets), per market: whether it is open and for which
// vendor types, how many people a basket takes, and the cut-off, top-up and
// payment windows (services/groupOrders/groupOrderSettings.js on the server;
// founder decisions 2 Nov 2026). The server decides every amount and every
// deadline; this screen only switches it and sets the windows. It ships OFF.
import React, { useEffect, useState } from "react";
import { notify } from "../../utilities/notify";
import SettingsPanel from "../../components/SettingsPanel";
import { THEME as C } from "../../components/dashboardTheme";
import { apiGetGroupOrderingSettings, apiUpdateGroupOrderingSettings } from "../../api";
import { field, LastChanged, ReadOnlyNotice, SaveButton } from "./settingsUi";
import SettingHelp from "./settingsHelp";
import { AppButton } from "../../components/ui";

const MARKETS = [
  { code: "UG", label: "Uganda" },
  { code: "ZM", label: "Zambia" },
];
const VERTICAL_LABEL = { restaurant: "Restaurants" };
const SOURCE_LABEL = { setting: "saved here", env: "server environment", shipped: "shipped default" };

// The number fields, in the order shown, with their help topic and unit.
export const NUMBER_FIELDS = [
  { key: "maxMembers", label: "Most people in one basket", unit: "people", help: "groupOrderingMaxMembers" },
  { key: "cutoffDefaultMinutes", label: "Cut-off the host starts with", unit: "minutes", help: "groupOrderingCutoff" },
  { key: "cutoffMinMinutes", label: "Shortest cut-off a host may pick", unit: "minutes", help: "groupOrderingCutoff" },
  { key: "cutoffMaxMinutes", label: "Longest cut-off a host may pick", unit: "minutes", help: "groupOrderingCutoff" },
  { key: "topUpMinutes", label: "Time to reach the minimum after the cut-off", unit: "minutes", help: "groupOrderingTopUp" },
  { key: "memberPayMinutes", label: "Time for members to pay their shares", unit: "minutes", help: "groupOrderingMemberPay" },
  { key: "hostPayMinutes", label: "Time for the host to pay", unit: "minutes", help: "groupOrderingHostPay" },
];

// The form keeps only what the operator typed; a blank number means "use the
// server default" and is sent as null, which clears the saved override.
const formFrom = (data) => ({
  enabledVerticals: [...(data?.enabledVerticals || [])],
  ...Object.fromEntries(
    NUMBER_FIELDS.map(({ key }) => [key, data?.overrides && key in data.overrides ? String(data.overrides[key]) : ""])
  ),
});

const sameList = (a = [], b = []) => [...a].sort().join(",") === [...b].sort().join(",");

const GroupOrderSettings = ({ canEdit = true }) => {
  const [country, setCountry] = useState("UG");
  const [data, setData] = useState(null);
  const [form, setForm] = useState(formFrom(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [reason, setReason] = useState("");

  const load = async (cc) => {
    setLoading(true);
    try {
      const res = await apiGetGroupOrderingSettings(cc);
      setData(res.data);
      setForm(formFrom(res.data));
    } catch (err) {
      notify.fromError(err, "Could not load the Order together settings");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(country); }, [country]);

  const defaults = data?.defaults || {};
  const supported = data?.supportedVerticals || ["restaurant"];
  const numbersValid = NUMBER_FIELDS.every(({ key }) => {
    const text = String(form[key] || "").trim();
    return text === "" || (Number.isInteger(Number(text)) && Number(text) > 0);
  });
  const reasonValid = reason.trim().length >= 4;
  const unchanged = data && JSON.stringify(formFrom(data)) === JSON.stringify(form);

  // A switch equal to the server default is sent as null, so the market keeps
  // following the default (an env change included).
  const patch = () => ({
    enabledVerticals: sameList(form.enabledVerticals, defaults.enabledVerticals) ? null : form.enabledVerticals,
    ...Object.fromEntries(
      NUMBER_FIELDS.map(({ key }) => {
        const text = String(form[key] || "").trim();
        return [key, text === "" ? null : Number(text)];
      })
    ),
  });

  const save = async (e) => {
    e.preventDefault();
    if (!numbersValid) return notify.error("Enter whole numbers", "Leave a field blank to use the server default.");
    if (!reasonValid) return notify.error("Give a reason for this change", "It is recorded in the audit log with your name.");
    setSaving(true);
    try {
      const res = await apiUpdateGroupOrderingSettings({ ...patch(), reason: reason.trim() }, country);
      setData(res.data);
      setForm(formFrom(res.data));
      setReason("");
      setSavedAt(Date.now());
      notify.success(
        "Order together saved",
        `${MARKETS.find((m) => m.code === country)?.label || country}: ${res.data.enabled ? `open for ${res.data.enabledVerticals.map((v) => VERTICAL_LABEL[v] || v).join(", ")}` : "off"}. The apps and the website read it within about a minute.`
      );
    } catch (err) {
      notify.fromError(err, "Could not save the Order together settings");
    } finally {
      setSaving(false);
    }
  };

  const disabled = !canEdit || saving || loading;
  const sourceOf = (key) => SOURCE_LABEL[data?.sources?.[key]] || "shipped default";
  const toggleVertical = (vertical, on) =>
    setForm((f) => ({
      ...f,
      enabledVerticals: on ? [...new Set([...f.enabledVerticals, vertical])] : f.enabledVerticals.filter((v) => v !== vertical),
    }));

  return (
    <SettingsPanel
      icon="fa-people-group"
      title="Order together"
      subtitle={loading ? "Loading..." : data?.enabled ? `On for ${data.enabledVerticals.map((v) => VERTICAL_LABEL[v] || v).join(", ")}, up to ${data.maxMembers} people` : "Off in this market"}
    >
      {!canEdit && <ReadOnlyNotice level="manage" />}
      <form onSubmit={save}>
        <SettingsPanel.Section title="What this does" icon="fa-circle-info">
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: C.textMid }}>
            A customer opens a group basket at one restaurant, shares a link, and friends add their own items. At the
            cut-off the basket becomes one order to the restaurant, delivered by one rider to the host, with each line
            labelled with the person who chose it. The host chooses whether they pay for everything or each person pays
            their own share; the delivery fee is split equally among the people with items. The server prices
            everything, keeps every deadline and refunds paid shares when a basket cannot be placed.
            <SettingHelp topic="groupOrderingOverview" />
          </p>
        </SettingsPanel.Section>

        <SettingsPanel.Section title="Settings for each market" icon="fa-sliders-h">
          <div style={field.grid(2)}>
            <div>
              <label style={field.label}>Market<SettingHelp topic="groupOrderingMarket" /></label>
              <select style={field.input} value={country} onChange={(e) => setCountry(e.target.value)} disabled={saving}>
                {MARKETS.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label style={field.label}>Open for<SettingHelp topic="groupOrderingVerticals" /></label>
              {supported.map((vertical) => (
                <label key={vertical} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.text }}>
                  <input
                    type="checkbox"
                    checked={form.enabledVerticals.includes(vertical)}
                    disabled={disabled}
                    onChange={(e) => toggleVertical(vertical, e.target.checked)}
                  />
                  {VERTICAL_LABEL[vertical] || vertical}
                </label>
              ))}
              <div style={field.help}>
                Server default {(defaults.enabledVerticals || []).length ? (defaults.enabledVerticals || []).join(", ") : "off"}. Now from the {sourceOf("enabledVerticals")}.
              </div>
            </div>
            {NUMBER_FIELDS.map(({ key, label, unit, help }) => (
              <div key={key}>
                <label style={field.label}>
                  {label} ({unit})
                  <SettingHelp topic={help} />
                </label>
                <input
                  style={field.input}
                  type="number" min="1" step="1" inputMode="numeric"
                  placeholder={`Server default ${defaults[key] ?? ""}`}
                  value={form[key]}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  disabled={disabled}
                />
                <div style={field.help}>Server default {defaults[key] ?? "not set"}. Leave blank to use it. Now from the {sourceOf(key)}.</div>
              </div>
            ))}
            {canEdit && (
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={field.label}>Reason for this change<SettingHelp topic="groupOrderingReason" /></label>
                <input
                  style={field.input}
                  value={reason}
                  maxLength={500}
                  required
                  minLength={4}
                  placeholder="For example: pilot with restaurants in Kampala"
                  onChange={(e) => setReason(e.target.value)}
                  disabled={saving || loading}
                />
                <div style={field.help}>Enter at least 4 characters to save. Recorded in the audit log with your name, the market and the values before and after.</div>
              </div>
            )}
          </div>
          <div role="status" style={field.help}>{!unchanged && !loading ? (reasonValid ? "Unsaved changes — ready to save." : "Unsaved changes — enter a reason (at least 4 characters), then save.") : ""}</div>
          <div style={field.actions}>
            {canEdit && <SaveButton saving={saving} disabled={!numbersValid || unchanged || loading} />}
            {canEdit && data && Object.keys(data.overrides || {}).length > 0 && (
              <AppButton
                variant="quiet"
                icon="fas fa-rotate-left"
                onClick={() => setForm({ ...formFrom(null), enabledVerticals: [...(defaults.enabledVerticals || [])] })}
                disabled={saving}
              >
                Use the server defaults
              </AppButton>
            )}
            <div style={{ marginLeft: "auto" }}>
              <LastChanged keys={[`group_ordering_${country}`]} refreshToken={savedAt} />
            </div>
          </div>
        </SettingsPanel.Section>
      </form>
    </SettingsPanel>
  );
};

export default GroupOrderSettings;
